import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp,
  Search,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Briefcase,
  AlertCircle,
  User,
  RefreshCw,
  Globe
} from 'lucide-react';
import adminService from '../../services/admin.service';
import type { ConsultantService } from '../../services/consultants.service';
import ApprovalStatusBadge from '../../components/common/ApprovalStatusBadge';
import { API_ENDPOINTS } from '../../config/api';

interface PrestationWithCreneaux extends Omit<ConsultantService, 'creneaux'> {
  creneaux?: Array<{
    id: number;
    creneau_date: string;
    start_time: string;
    end_time: string;
    is_available: boolean;
    is_booked?: boolean;
  }>;
  reservationsCount?: number;
}

const PrestationsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [prestations, setPrestations] = useState<PrestationWithCreneaux[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus] = useState('all');
  const [filterApproval, setFilterApproval] = useState(searchParams.get('filter') || 'all');
  const [selectedPrestation, setSelectedPrestation] = useState<PrestationWithCreneaux | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [prestationDetails, setPrestationDetails] = useState<PrestationWithCreneaux | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [notification, setNotification] = useState<{
    title: string;
    subtitle: string;
    kind: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  useEffect(() => {
    fetchPrestations();
  }, [filterApproval]);

  useEffect(() => {
    if (autoRefreshEnabled) {
      const interval = setInterval(() => {
        fetchPrestations();
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefreshEnabled, filterApproval]);

  const fetchPrestations = async () => {
    try {
      setLoading(true);
      const response = await adminService.getConsultantServices();
      setPrestations(response);
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible de charger les prestations',
        kind: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPrestationDetails = async (id: number) => {
    try {
      setLoadingDetails(true);
      const response = await adminService.getConsultantServiceDetails(id);
      setPrestationDetails(response);
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible de charger les détails de la prestation',
        kind: 'error'
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await adminService.approveConsultantService(id);
      setNotification({
        title: 'Succès',
        subtitle: t('admin.prestations.messages.approved'),
        kind: 'success'
      });
      fetchPrestations();
      setIsModalOpen(false);
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible d\'approuver la prestation',
        kind: 'error'
      });
    }
  };

  const handleReject = async (id: number) => {
    try {
      await adminService.rejectConsultantService(id, rejectReason);
      setNotification({
        title: 'Succès',
        subtitle: 'Prestation refusée avec succès',
        kind: 'success'
      });
      fetchPrestations();
      setIsModalOpen(false);
      setIsRejectModalOpen(false);
      setRejectReason('');
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible de refuser la prestation',
        kind: 'error'
      });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminService.deleteConsultantService(id);
      setNotification({
        title: 'Succès',
        subtitle: 'Prestation supprimée avec succès',
        kind: 'success'
      });
      fetchPrestations();
      setIsModalOpen(false);
      setIsDeleteModalOpen(false);
    } catch (error) {
      setNotification({
        title: 'Erreur',
        subtitle: 'Impossible de supprimer la prestation',
        kind: 'error'
      });
    }
  };

  const filteredPrestations = prestations.filter(prestation => {
    const matchesSearch = prestation.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         prestation.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         prestation.consultant_names?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || prestation.status === filterStatus;
    const matchesApproval = filterApproval === 'all' || prestation.status === filterApproval;
    
    return matchesSearch && matchesStatus && matchesApproval;
  });

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'presentiel':
        return <User className="h-4 w-4" />;
      case 'distance':
        return <Globe className="h-4 w-4" />;
      case 'hybride':
        return <Calendar className="h-4 w-4" />;
      default:
        return <Briefcase className="h-4 w-4" />;
    }
  };

  const getFormatLabel = (format: string) => {
    switch (format) {
      case 'presentiel':
        return 'En présentiel';
      case 'distance':
        return 'À distance';
      case 'hybride':
        return 'Hybride';
      default:
        return format;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5); // HH:MM format
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-6 w-6" />
            {t('admin.prestations.title')}
          </h1>
          <p className="text-gray-600 mt-1">
            {t('admin.prestations.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* <button
            onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              autoRefreshEnabled
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <RefreshCw className={`h-4 w-4 ${autoRefreshEnabled ? 'animate-spin' : ''}`} />
            Auto-refresh {autoRefreshEnabled ? 'ON' : 'OFF'}
          </button> */}
          <button
            onClick={fetchPrestations}
            className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            {t('admin.prestations.refresh')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('admin.prestations.totalPrestations')}</p>
              <p className="text-2xl font-bold text-gray-900">{prestations.length}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('admin.prestations.pending')}</p>
              <p className="text-2xl font-bold text-yellow-600">
                {prestations.filter(p => p.status === 'pending').length}
              </p>
            </div>
            <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('admin.prestations.approved')}</p>
              <p className="text-2xl font-bold text-green-600">
                {prestations.filter(p => p.status === 'approved').length}
              </p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('admin.prestations.refused')}</p>
              <p className="text-2xl font-bold text-red-600">
                {prestations.filter(p => p.status === 'refused').length}
              </p>
            </div>
            <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder={t('admin.prestations.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={filterApproval}
              onChange={(e) => setFilterApproval(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            >
              <option value="all">{t('admin.prestations.allStatuses')}</option>
              <option value="pending">{t('admin.prestations.pending')}</option>
              <option value="approved">{t('admin.prestations.approved')}</option>
              <option value="refused">{t('admin.prestations.refused')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Prestations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.prestations.table.prestation')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.prestations.table.consultant')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.prestations.table.format')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.prestations.table.creneaux')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.prestations.table.reservations')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.prestations.table.views')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.prestations.table.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.prestations.table.createdAt')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('admin.prestations.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPrestations.map((prestation) => (
                <tr key={prestation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        {prestation.banner_image ? (
                          <img
                            className="h-10 w-10 rounded-lg object-cover"
                            src={prestation.banner_image.startsWith('http') ? prestation.banner_image : `${API_ENDPOINTS.UPLOADS}/${prestation.banner_image}`}
                            alt={prestation.title}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                            <Briefcase className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {prestation.title}
                        </div>
                        <div className="text-sm text-gray-500">
                          {prestation.reference}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 break-words whitespace-normal max-w-xs">
                      {prestation.consultant_names || 'Non spécifié'}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {prestation.training_center?.name || prestation.training_center?.company_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getFormatIcon(prestation.format)}
                      <span className="text-sm text-gray-900">
                        {getFormatLabel(prestation.format)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {prestation.creneaux?.length || 0} créneaux
                    </div>
                    {prestation.creneaux && prestation.creneaux.length > 0 && (
                      <div className="text-sm text-gray-500">
                        Prochain: {formatDate(prestation.creneaux[0].creneau_date)} {formatTime(prestation.creneaux[0].start_time)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">
                        {prestation.reservationsCount || 0}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Eye className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{prestation.views || 0}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ApprovalStatusBadge status={prestation.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(prestation.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setSelectedPrestation(prestation);
                          fetchPrestationDetails(Number(prestation.id));
                          setIsModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {prestation.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(Number(prestation.id))}
                            className="text-green-600 hover:text-green-900 p-1 rounded"
                            title="Approuver"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedPrestation(prestation);
                              setIsRejectModalOpen(true);
                            }}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Refuser"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => {
                          setSelectedPrestation(prestation);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {isModalOpen && prestationDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">{t('admin.prestations.details.title')}</h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {loadingDetails ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('admin.prestations.details.generalInfo')}</h3>
                      <div className="space-y-2">
                        <p><span className="font-medium">{t('admin.prestations.details.titleLabel')}</span> {prestationDetails.title}</p>
                        <p><span className="font-medium">{t('admin.prestations.details.referenceLabel')}</span> {prestationDetails.reference}</p>
                        <p><span className="font-medium">{t('admin.prestations.details.formatLabel')}</span> {getFormatLabel(prestationDetails.format)}</p>
                        <p><span className="font-medium">{t('admin.prestations.details.durationLabel')}</span> {prestationDetails.estimated_duration}</p>
                        <p><span className="font-medium">{t('admin.prestations.details.languagesLabel')}</span> {prestationDetails.language}</p>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('admin.prestations.table.consultant')}</h3>
                      <div className="space-y-2">
                        <p><span className="font-medium">{t('admin.prestations.details.consultantLabel')}</span> {prestationDetails.consultant_names}</p>
                        <p><span className="font-medium">{t('admin.prestations.details.centerLabel')}</span> {prestationDetails.training_center?.name}</p>
                        <p><span className="font-medium">{t('admin.prestations.details.locationLabel')}</span> {prestationDetails.city}, {typeof prestationDetails.country === 'string' ? prestationDetails.country : prestationDetails.country?.name}</p>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('admin.prestations.details.description')}</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{prestationDetails.full_description}</p>
                  </div>

                  {/* Skills and Objectives */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {prestationDetails.skills_gained && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('admin.prestations.details.skills')}</h3>
                        <p className="text-gray-700 whitespace-pre-wrap">{prestationDetails.skills_gained}</p>
                      </div>
                    )}
                    {prestationDetails.learning_objectives && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('admin.prestations.details.objectives')}</h3>
                        <p className="text-gray-700 whitespace-pre-wrap">{prestationDetails.learning_objectives}</p>
                      </div>
                    )}
                  </div>

                  {/* Target Audience */}
                  {prestationDetails.target_audience && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('admin.prestations.details.targetAudience')}</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{prestationDetails.target_audience}</p>
                    </div>
                  )}

                  {/* Créneaux */}
                  {prestationDetails.creneaux && prestationDetails.creneaux.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('admin.prestations.details.creneauxTitle')}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {prestationDetails.creneaux.map((creneau) => (
                          <div key={creneau.id} className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-gray-900">
                                {formatDate(creneau.creneau_date)}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                creneau.is_available 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {creneau.is_available ? t('admin.prestations.details.available') : t('admin.prestations.details.occupied')}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600">
                              {formatTime(creneau.start_time)} - {formatTime(creneau.end_time)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                    {prestationDetails.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(Number(prestationDetails.id))}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          {t('admin.prestations.details.approve')}
                        </button>
                        <button
                          onClick={() => {
                            setIsRejectModalOpen(true);
                            setIsModalOpen(false);
                          }}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          {t('admin.prestations.details.refuse')}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      {t('admin.prestations.details.close')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {isRejectModalOpen && selectedPrestation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.prestations.reject.title')}</h3>
              <p className="text-gray-600 mb-4">
                {t('admin.prestations.reject.message', { title: selectedPrestation.title })}
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('admin.prestations.reject.reason')}
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  placeholder={t('admin.prestations.reject.reasonPlaceholder')}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsRejectModalOpen(false);
                    setRejectReason('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  {t('admin.prestations.reject.cancel')}
                </button>
                <button
                  onClick={() => handleReject(Number(selectedPrestation.id))}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('admin.prestations.reject.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {isDeleteModalOpen && selectedPrestation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('admin.prestations.delete.title')}</h3>
              <p className="text-gray-600 mb-4">
                {t('admin.prestations.delete.message', { title: selectedPrestation.title })}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  {t('admin.prestations.delete.cancel')}
                </button>
                <button
                  onClick={() => handleDelete(Number(selectedPrestation.id))}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('admin.prestations.delete.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
          notification.kind === 'success' ? 'bg-green-100 border border-green-200 text-green-800' :
          notification.kind === 'error' ? 'bg-red-100 border border-red-200 text-red-800' :
          notification.kind === 'warning' ? 'bg-yellow-100 border border-yellow-200 text-yellow-800' :
          'bg-blue-100 border border-blue-200 text-blue-800'
        }`}>
          <div className="flex items-center gap-2">
            {notification.kind === 'success' && <CheckCircle className="h-5 w-5" />}
            {notification.kind === 'error' && <XCircle className="h-5 w-5" />}
            {notification.kind === 'warning' && <AlertCircle className="h-5 w-5" />}
            {notification.kind === 'info' && <AlertCircle className="h-5 w-5" />}
            <div>
              <p className="font-medium">{notification.title}</p>
              <p className="text-sm">{notification.subtitle}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrestationsPage;
